//Libraries
import { FC, useMemo, useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { get, isString, isNumber } from "lodash";
import { Modal, Column, PrimaryButton, Button, Table, NakedButton } from "@cb/apricot-react";
import { styled } from "@mui/system";
import { CircularProgress, Dialog, TextField, InputAdornment, TablePagination } from "@mui/material";
import { getFileLink } from 'src/utils';

//Providers
import { useAuth } from "src/contexts";
import { useToasts } from "src/hooks";

//Typing
import { APP_URLS } from "src/constants";
import { processStatusOptions } from "src/constants";
import { TAIFormProcessStatus, ISummary, IMaterial } from "src/types";

// Processing
import { statusUpdate } from "../../services/schoolRequest";
import { formatDate, formatProcessStatus } from "src/utils";
import { disableDismiss } from "src/generic/utils/modal";
import { cleanMessage } from "../../utils/string";
import { XpmRejectForm } from "src/containers/school/operations/xpmRejectForm";

// Icons
import AttachFileIcon from "@mui/icons-material/AttachFile";
import Search from "@mui/icons-material/Search";
import Clear from "@mui/icons-material/Clear";
import MuiIconButton from "@mui/material/IconButton";

// Components
import { ApprovedModal, DeniedModal, NeedMoreInfoModal, VettingNotesModal, ViewVettingNotesModal } from "./modals";
import { IconButton } from "src/generic/component";
import { StatusHistoryModal } from "./elements/status";
import { WordBreak, Typography } from "src/components";
import { RequestActions } from "./elements/actions";
import { VettingNotes } from "./elements/note";

const Select = styled("select")`
  width: 100%;
  height: 3em;
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  padding: 0 12px;
`;

const StatusCell = styled("div")`
  display: flex;
  flex-direction: column;
`;

const MaterialList = styled("ul")`
  list-style: none;
  padding-left: 8px;
`;

const ActionButton = styled(NakedButton)`
  padding: 0;
`;

interface SummaryProps {
  data: ISummary[];
  type: "ai" | "di";
  statuses: string[];
  filters: string[];
  disableViewer: boolean;
  onViewRequest: (data: ISummary, filters: {search: string;requestType: string; ingestionPathCd?: string }) => void;
  onUpdateRequest: (data: ISummary) => void;
}

let status: TAIFormProcessStatus = "submitted";
interface StatusIndex {
  [key: string]: ISummary[];
}

const activeButton = { borderColor: "#dfbe2a", backgroundColor: "#dfbe2a", color: "white", boxShadow: "none", textDecoration: "none" };
const toggleButton = { marginRight: "10px", padding: "7px 15px" };

const handleMaterialClick = async (material: IMaterial) => {
    console.log('handleMaterialClick');
    const url = await getFileLink(material);
    window.open(url, "_blank");
};

function sortRecords<T>(sortField: string, sortAscending: boolean, sortable: T[]) {
  return sortable.sort((a, b) => {
    const valueA = get(a, sortField);
    const valueB = get(b, sortField);
    if (valueA === undefined) {
      return -1;
    } else if (valueB === undefined) {
      return 1;
    } else if (isString(valueA) && isString(valueB)) {
      if (valueA.toLowerCase() > valueB.toLowerCase()) return sortAscending ? 1 : -1;
      if (valueB.toLowerCase() > valueA.toLowerCase()) return sortAscending ? -1 : 1;
      return 0;
    } else if (isNumber(valueA) && isNumber(valueB)) {
      return sortAscending ? valueA - valueB : valueB - valueA;
    } else {
      throw Error("Cannot sortField types other than strings or numbers.");
    }
  });
}

const searchRecords = (search: string, type: string, filterable: ISummary[]) => {
  let filtered = filterable;
  if (search) {
    const fragments = search.split(" ");
    filtered = filtered.filter((item) => {
      let matched = true;
      for (const fragment of fragments) {
        let foundName: boolean = false;
        let foundCode: boolean = false;
        let foundSubmitter: boolean = false;
        let foundEmail: boolean = false;
        let foundCountry: boolean = false;
        let foundRegion: boolean = false;
        foundName = item.schoolName?.toLowerCase().includes(fragment.toLowerCase()) || false;
        foundSubmitter = item.creator?.toLowerCase().includes(fragment.toLowerCase()) || false;
        foundEmail = item.creatorEmail?.toLowerCase().includes(fragment.toLowerCase()) || false;

        const codeTest = /^[a-zA-Z]{2,3}$/;
        const isCodeFragment = codeTest.test(fragment);
        if (isCodeFragment && item.country) foundCountry = item.country.toLowerCase().includes(fragment.toLowerCase()) || false;

        if (item.region) {
          const isRegionCode = codeTest.test(item.region);
          if (isRegionCode) {
            if (isCodeFragment) {
              foundRegion = item.region.toLowerCase().includes(fragment.toLowerCase()) || false;
            }
          } else {
            // Check if fragment is found in the region in general, if so, we can stop
            foundRegion = item.region.toLowerCase().includes(fragment.toLowerCase()) || false;
            if (!foundRegion && /\s/.test(item.region)) {
              // If not found, we wanna try to fabricate a reasonable code by checking for spaces and taking the first character and assembling a code
              if (isCodeFragment) {
                let generatedCode = "";
                const parts = item.region.split(" ");
                for (const part of parts) {
                  generatedCode += part.length ? part[0] : "";
                }
                if (generatedCode) {
                  foundRegion = generatedCode.toLowerCase().includes(fragment.toLowerCase()) || false;
                }
              }
            }
          }
        }

        /** check type and based on it , aiCode or diCode */
        if (/^[0-9]+/.test(fragment)) foundCode = (type === "ai" ? item.aiCode?.includes(fragment) : item.diCode?.includes(fragment)) || false;
        if (!foundName && !foundCode && !foundSubmitter && !foundEmail && !foundCountry && !foundRegion) {
          matched = false;
          break;
        }
      }

      return matched;
    });
  }

  return filtered;
};

const SummarySearch: FC<SummaryProps> = ({ data, type, statuses, disableViewer, filters, onViewRequest, onUpdateRequest }) => {
  const validStatuses = statuses?.length ? processStatusOptions.filter((item) => statuses.includes(item.value)) : processStatusOptions;

  const { lastName, firstName } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [isApprovedModalOpen, setIsApprovedModalOpen] = useState(false);
  const [isDeniedModalOpen, setIsDeniedModalOpen] = useState(false);
  const [isNeedMoreInfoModalOpen, setIsNeedMoreInfoModalOpen] = useState(false);
  const [isAddVettingNotesOpen, setAddVettingNotesOpen] = useState(false);
  const [isViewVettingNotesOpen, setViewVettingNotesOpen] = useState(false);
  const [isStatusHistoryOpen, setStatusHistoryOpen] = useState(false);
  const [loading, setLoading] = useState("");
  const [currentRequest, setCurrentRequest] = useState<ISummary | null>(null);
  const [currentProcessStatus, setCurrentProcessStatus] = useState<TAIFormProcessStatus>(status);
  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState("submissionDate");
  const [sortAscending, setSortAscending] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, perPage: 10 });
  const [requestType, setRequestType] = useState("all");
  const [ingestionPathCd, setIngestionPathCd] = useState("all");
  const { render: renderToasts, toastErrorMessage } = useToasts();

  useEffect(() => {
    if (location.state) {
      const { search, requestType, ingestionPathCd } = location.state;
      if (search !== undefined) setSearch(search);
      if (requestType !== undefined) setRequestType(requestType);
      if (ingestionPathCd !== undefined) setIngestionPathCd(ingestionPathCd.toString());
      navigate(location.pathname, { replace: true });
    }
  }, []);

  const submitted = data?.filter((item) => !!item.submission && !!item.submissionDate) ?? [];
  const statusIndex: StatusIndex = {};
  const matchedRecords = useMemo<ISummary[]>(() => {
    let filtered = searchRecords(search, type, submitted);
    if (requestType !== "all") {
      filtered = filtered.filter((item) => item.requestType === requestType);
    }

    if (ingestionPathCd !== "all") {
      filtered = filtered.filter((item) => {
        let ingestionCd = item?.ingestionPathCd?.toString() || "1";
        return ingestionCd === ingestionPathCd;
      });
    }

    return sortRecords<ISummary>(sortField, sortAscending, filtered);
  }, [data, search, requestType, ingestionPathCd, sortField, sortAscending]);

  for (const status of validStatuses) {
    statusIndex[status.value] = matchedRecords.filter((each: ISummary) => each.processStatus === status.value) ?? [];
  }

  const records = statusIndex[currentProcessStatus] || [];

  const AIFormStatus = (request: ISummary) => {
    const comments = request?.processStatusComments || "";
    const status = request?.processStatus;

    const requireTestingPlan = status === "reopen" && request?.requireTestingPlan;
    let commentLabel;
    if (status === "reopen") {
      commentLabel = "Additional Information Required";
    } else if (status === "rejected") {
      commentLabel = "Denial Reason";
    } else {
      commentLabel = "Reviewer Comments";
    }
    const statusLabel = formatProcessStatus(status) || "N/A";

    return (
      <>
        <div>
          <span>Status :</span> <span style={{ fontWeight: 500 }}> {statusLabel}</span>&nbsp;
          <ActionButton
            onClick={() => {
              setStatusHistoryOpen(true);
              setCurrentRequest(request);
            }}
          >
            History ({request.statusUpdates.length})
          </ActionButton>
        </div>
        {request.aiCode && (
          <div>
            <span>School Code is</span> {request.aiCode}.
          </div>
        )}
        {request.aiLevelInd && (
          <div>
            <span>Approved School Level is</span> {request.aiLevelInd}.
          </div>
        )}
        {request.diCode && (
          <div>
            <span>Institution Code is</span> {request.diCode}.
          </div>
        )}
        {comments.length > 0 && (
          <div className="div-wordwrap" style={{ marginTop: 10 }}>
            <span style={{ color: "#7b7b7b" }}>{commentLabel} :</span>
            <div style={{ fontWeight: 500, maxWidth: "500px", whiteSpace: "pre-line" }}>{cleanMessage(comments)}</div>
          </div>
        )}

        {requireTestingPlan && <div className="div-wordwrap">Testing Plan Updates Required.</div>}
        <div style={{ marginBottom: "4px" }}></div>

        {status === "completed" && request.operationSubmission && (
          <IconButton
            onClick={() => {
              operationClick(request);
            }}
          >
            View Change
          </IconButton>
        )}
      </>
    );
  };

  const handleSearch = (event: any) => {
    setPagination({ perPage: pagination.perPage, page: 1 });
    setSearch(event.target.value);
  };

  const handlePageChange = (event: any, page: any) => {
    setPagination({ perPage: pagination.perPage, page: page + 1 });
  };

  const handlePerPageChange = (event: any) => {
    setPagination({ perPage: event.target.value, page: 1 });
  };

  const handleClickDenied = (request: ISummary) => {
    setIsDeniedModalOpen(true);
    setCurrentRequest(request);
  };

  const handleClickNeedMoreInfo = (request: ISummary) => {
    setIsNeedMoreInfoModalOpen(true);
    setCurrentRequest(request);
  };

  const handleClickAddVettingNotes = (request: ISummary) => {
    setAddVettingNotesOpen(true);
    setCurrentRequest(request);
  };

  const handleClickViewVettingNotes = (request: ISummary) => {
    setViewVettingNotesOpen(true);
    setCurrentRequest(request);
  };

  const handleXpmDenied = async (request: ISummary) => {
    setRejectOpen(true);
    setCurrentRequest(request);
  };
  const [isRejectOpen, setRejectOpen] = useState(false);

  const handleStatusUpdate = async (request: ISummary, status: TAIFormProcessStatus) => {
    setLoading(`Updating Status`);
    try {
      const updated = await statusUpdate(request.userId, request.categoryId, {
        user: lastName + ', ' + firstName, //XP-24778:userNames now start with 'Z9Z9P',replacing it with lastName, firstName
        status: status,
      });
      request.updateDate = updated.time;
      onUpdateRequest(request);
    } catch (error: any) {
      console.log("handleChangeStatusAndSchoolInfo : statusUpdate call failed :" + error.stack);
      toastErrorMessage(error.message);
    }
    setLoading("");
  };

  const Loader = (
    <Modal
      title={
        <>
          <CircularProgress /> {loading}
        </>
      }
      closeButton={false}
      actions={[]}
      open={loading ? true : false}
      onClose={() => {
        setLoading("");
      }}
      withHeader
    ></Modal>
  );

  const operationClick = (school: ISummary) => {
    navigate(APP_URLS.OPERATION_FORM, {
      state: {
        summary: school,
        type: type,
      },
    });
  };

  return (
    <>
      {renderToasts()}

      {Loader}

      <Dialog fullWidth={true} maxWidth={"md"} open={isRejectOpen} onClose={disableDismiss}>
        <div style={{ padding: "20px" }}>
          {currentRequest && (
            <XpmRejectForm
              onComplete={(summary) => {
                setRejectOpen(false);
                onUpdateRequest(summary);
              }}
              onClose={() => {
                setRejectOpen(false);
              }}
              summary={currentRequest}
            />
          )}
        </div>
      </Dialog>

      <StatusHistoryModal
        isOpen={isStatusHistoryOpen}
        onClose={() => {
          setStatusHistoryOpen(false);
        }}
        summary={currentRequest}
      />

      <ApprovedModal
        isOpen={isApprovedModalOpen}
        onClose={() => {
          setIsApprovedModalOpen(false);
        }}
        onComplete={(request: ISummary) => {
          setIsApprovedModalOpen(false);
          onUpdateRequest(request);
        }}
        request={currentRequest}
      />

      <DeniedModal
        isOpen={isDeniedModalOpen}
        request={currentRequest}
        onComplete={(request: ISummary) => {
          setIsDeniedModalOpen(false);
          onUpdateRequest(request);
        }}
        onClose={() => {
          setIsDeniedModalOpen(false);
        }}
      />

      <NeedMoreInfoModal
        isOpen={isNeedMoreInfoModalOpen}
        request={currentRequest}
        onClose={() => setIsNeedMoreInfoModalOpen(false)}
        onComplete={(request: ISummary) => {
          setIsNeedMoreInfoModalOpen(false);
          onUpdateRequest(request);
        }}
      />

      <VettingNotesModal
        isOpen={isAddVettingNotesOpen}
        request={currentRequest}
        onClose={() => setAddVettingNotesOpen(false)}
        onComplete={(request: ISummary) => {
          setAddVettingNotesOpen(false);
          onUpdateRequest(request);
        }}
      />

      <ViewVettingNotesModal isOpen={isViewVettingNotesOpen} onClose={() => setViewVettingNotesOpen(false)} summary={currentRequest} />

      <TextField
        size="small"
        variant="outlined"
        label="Search Requests"
        style={{ marginRight: 10, marginBottom: 10, backgroundColor: "#FFF" }}
        value={search}
        placeholder="Type to Search..."
        onChange={handleSearch}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
            <Search />
              {search && (
                <MuiIconButton
                  size="small"
                  onClick={() => {
                    // Clear the text
                    handleSearch({ target: { value: "" } } as React.ChangeEvent<HTMLInputElement>);
                  }}
                >
                  <Clear />
                </MuiIconButton>
              )}
            </InputAdornment>
          ),
        }}
      />
      <span style={{ fontWeight: 600, paddingRight: "16px" }}>Filter By:</span>
      <Button
        style={requestType === "update" ? { ...activeButton, ...toggleButton } : toggleButton}
        onClick={() => {
          setRequestType("update");
        }}
      >
        Update
      </Button>
      <Button
        style={requestType === "create" ? { ...activeButton, ...toggleButton } : toggleButton}
        onClick={() => {
          setRequestType("create");
        }}
      >
        Create
      </Button>
      <Button
        style={requestType === "all" ? { ...activeButton, ...toggleButton } : toggleButton}
        onClick={() => {
          setRequestType("all");
        }}
      >
        All
      </Button>

      {filters.includes("ingestionPath") && (
        <div>
          <span style={{ fontWeight: 600, paddingRight: "10px", paddingLeft: "200px" }}>Ingestion Path:</span>
          <Button
            style={ingestionPathCd === "1" ? { ...activeButton, ...toggleButton } : toggleButton}
            onClick={() => {
              setIngestionPathCd("1");
            }}
          >
            User Submissions
          </Button>
          <Button
            style={ingestionPathCd === "2" ? { ...activeButton, ...toggleButton } : toggleButton}
            onClick={() => {
              setIngestionPathCd("2");
            }}
          >
            Bulk(DEX)
          </Button>
          <Button
            style={ingestionPathCd === "all" ? { ...activeButton, ...toggleButton } : toggleButton}
            onClick={() => {
              setIngestionPathCd("all");
            }}
          >
            All
          </Button>
        </div>
      )}
      <br />
      <Table
        onSort={async (field: string, isAscending: boolean) => {
          setSortField(field);
          setSortAscending(isAscending);
        }}
        initialSort={{ field: sortField, ascending: sortAscending }}
        isExternalSort={true}
        a11yHighlightType="append"
        className="header-aligned-table"
        condensed
        data={records.slice(
          (pagination.page - 1) * pagination.perPage,
          pagination.page * pagination.perPage < records.length ? pagination.page * pagination.perPage : records.length
        )}
        striped
        sortType="inline"
        tableId="requestList"
        forceResize
        overflowX={true}
      >
        <Column
          field="submissionDate"
          sortable
          title="Submitted Requests"
          // @ts-ignore
          onValueFormat={(_value, data: ISummary) => {
            const reqLabel = data.requestType === "create" ? "Create" : "Update";
            return (
              <div>
                <div>Initial Submit: {formatDate(data.submittedDate || 0)}</div>
                {data.submissionDate !== data.submittedDate && <div>Last Submit: {formatDate(data.submissionDate || 0)}</div>}
                {type === "ai" && (
                  <div>
                    Institution Type: <strong>{data.appliedType === "aia" ? "Ambassador" : "Attending"}</strong>
                  </div>
                )}
                <div style={{ marginBottom: "10px" }}>
                  Request Type: <strong>{`${reqLabel}`}</strong>
                </div>
                <PrimaryButton disabled={disableViewer} onClick={() => onViewRequest(data, { search, requestType, ingestionPathCd, })}>
                  View {reqLabel}
                </PrimaryButton>
              </div>
            );
          }}
        />
        <Column
          field="schoolName"
          sortable
          title="Institution Information"
          // @ts-ignore
          onValueFormat={(_value, summary: ISummary) =>
            !summary ? (
              <CircularProgress />
            ) : (
              <div>
                <WordBreak>
                  Name: <strong>{summary.schoolName}</strong>
                </WordBreak>
                <WordBreak>
                  Submitted by: <strong>{summary.creator}</strong>
                </WordBreak>
                <WordBreak style={{ marginBottom: "10px" }}>
                  <strong>{summary.creatorEmail}</strong>
                </WordBreak>

                {summary.country && (
                  <WordBreak>
                    Country: <strong>{summary.country}</strong>
                  </WordBreak>
                )}

                {summary.region && (
                  <WordBreak>
                    Region: <strong>{summary.region}</strong>
                  </WordBreak>
                )}
                {summary.appliedLevel && summary.requestType === "create" && (
                  <WordBreak>
                    Level Applied: <strong>{summary.appliedLevel}</strong>
                  </WordBreak>
                )}
              </div>
            )
          }
        />
        <Column
          field="materials[0].time"
          title="Support Materials"
          // @ts-ignore
          onValueFormat={(_value, summary: ISummary) => {
              const materials = summary.materials || [];
              return (
                  <MaterialList>
                      {materials
                          .filter((material) => !material.fieldPath)
                          .map((material: IMaterial) => (
                              <li className="display-flex align-items-xs-center" key={material.fileName + material.time}>
                                  <AttachFileIcon />
                                  <div>
                                      <a
                                          key={material.s3key}
                                          href="#"
                                          onClick={e => {
                                              e.preventDefault();
                                              handleMaterialClick(material);
                                          }}
                                      >
                                          {material.fileName}
                                      </a>                        <WordBreak>
                                      <WordBreak>{material.description}</WordBreak>
                                      <WordBreak>Uploaded: {formatDate(material.time)}</WordBreak>
                                  </WordBreak>
                                  </div>
                              </li>
                          ))}
                  </MaterialList>
              );
          }}
        />
        <Column
          field="updateDate"
          sortable
          title="Last Update"
          // @ts-ignore
          onValueFormat={(_value, summary: ISummary) => <div>{formatDate(summary.updateDate)}</div>}
        />
        <Column
          field="adminVettingNotes"
          title="Internal Vetting Notes"
          // @ts-ignore
          onValueFormat={(_value, request: ISummary) => {
            return (
              <VettingNotes
                request={request}
                onClickAddVettingNotes={handleClickAddVettingNotes}
                onClickViewVettingNotes={handleClickViewVettingNotes}
              />
            );
          }}
        />
        <Column
          field="processStatus"
          title="Status"
          headerComponent={() => (
            <div className="display-flex align-items-xs-center">
              <span>Status:&nbsp;</span>
              <Select
                name="status"
                value={currentProcessStatus}
                onChange={(e) => {
                  status = e.target.value as TAIFormProcessStatus;
                  setCurrentProcessStatus(status);
                  setPagination({ perPage: pagination.perPage, page: 1 });
                }}
              >
                {validStatuses.map((item) => {
                  const records = statusIndex[item.value];
                  const count = records ? records.length : 0;
                  return <option value={item.value}>{item.label + `(${count})`}</option>;
                })}
              </Select>
            </div>
          )}
          // @ts-ignore
          onValueFormat={(_value, request: ISummary) => (
            <StatusCell>
              {AIFormStatus(request)}
              {RequestActions({
                request,
                type,
                onClickApprove: () => {
                  operationClick(request);
                },
                onClickDenied: handleClickDenied,
                onClickNeedMoreInfo: handleClickNeedMoreInfo,
                onStatusUpdate: handleStatusUpdate,
                onClickXpmDenied: handleXpmDenied,
                onClickOpsApproved: () => {
                  operationClick(request);
                },
              })}
            </StatusCell>
          )}
        />
      </Table>
      <TablePagination
        component="div"
        colSpan={21}
        rowsPerPageOptions={[10, 25, 50]}
        count={records.length}
        rowsPerPage={pagination.perPage}
        page={pagination.page - 1}
        SelectProps={{
          inputProps: {
            "aria-label": "rows per page",
          },
          native: true,
        }}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handlePerPageChange}
      />

      {!records.length && (
        <Typography align="center" variant="h4">
          No request list.
        </Typography>
      )}
    </>
  );
};

export default SummarySearch;

